import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from './Icon';
import { IconButton } from './IconButton';

export type ToastSeverity = 'success' | 'error' | 'info';

interface ToastInput {
  title: string;
  description?: string;
  severity?: ToastSeverity;
  /** Auto-dismiss after this many ms. Default 5000. Pass 0 to keep it open. */
  duration?: number;
}

interface ToastRecord extends Required<Omit<ToastInput, 'description'>> {
  id: number;
  description?: string;
}

interface ToastContextValue {
  show: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * useToast — fire toasts from anywhere inside <ToastProvider>.
 *
 * Returns { show, dismiss }. show() returns the toast id so the caller can
 * proactively dismiss it (e.g. when navigating away or replacing it).
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * ToastProvider — mount once near the app root. Renders a fixed top-right
 * stack via portal; toasts auto-dismiss after `duration` (default 5s).
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextIdRef = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (input: ToastInput): number => {
      const id = nextIdRef.current++;
      const record: ToastRecord = {
        id,
        title: input.title,
        description: input.description,
        severity: input.severity ?? 'info',
        duration: input.duration ?? 5000,
      };
      setToasts((prev) => [...prev, record]);
      return id;
    },
    [],
  );

  return (
    <ToastContext.Provider value={{ show, dismiss }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[];
  onDismiss: (id: number) => void;
}) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 1000,
        pointerEvents: 'none',
      }}
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>,
    document.body,
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastRecord;
  onDismiss: () => void;
}) {
  useEffect(() => {
    if (toast.duration <= 0) return;
    const handle = window.setTimeout(onDismiss, toast.duration);
    return () => window.clearTimeout(handle);
  }, [toast.duration, onDismiss]);

  const tone = SEVERITY[toast.severity];

  return (
    <div
      role="status"
      style={{
        pointerEvents: 'auto',
        minWidth: 280,
        maxWidth: 400,
        background: 'var(--surface, #fff)',
        border: '1px solid var(--border, #e5e7eb)',
        borderLeft: `3px solid ${tone.accent}`,
        borderRadius: 'var(--radius-md, 8px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        animation: 'toast-in 160ms ease-out',
      }}
    >
      <Icon name={tone.icon} size={14} style={{ color: tone.accent, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1, #111)' }}>
          {toast.title}
        </div>
        {toast.description && (
          <div style={{ fontSize: 12, color: 'var(--text-3, #6b7280)', marginTop: 2 }}>
            {toast.description}
          </div>
        )}
      </div>
      <IconButton icon="x" iconSize={12} size="sm" label="Dismiss" onClick={onDismiss} />
    </div>
  );
}

const SEVERITY: Record<ToastSeverity, { accent: string; icon: 'check' | 'alert' | 'info' }> = {
  success: { accent: '#10b981', icon: 'check' },
  error: { accent: '#ef4444', icon: 'alert' },
  info: { accent: '#3b82f6', icon: 'info' },
};
